import React, { useId } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FOCUS_RING_ONLY, FIELD_LABEL_CLASS, FIELD_HELP_CLASS } from './FieldShell';

/**
 * The switch.
 *
 * It was a bare `<button>` with no `role`, no `aria-checked` and no way to label
 * itself - a screen reader announced it as an unlabelled button whose state was
 * invisible. Every one of its eighteen call sites therefore drew its own label
 * text beside it, each with slightly different spacing.
 *
 * `role="switch"` + `aria-checked` is what makes it a switch to assistive tech.
 * `label`/`description`/`disabled`/`size` are additive: the existing
 * `{enabled, onToggle}` call sites are untouched and render the bare switch
 * exactly as before, now with the champagne accent instead of `bg-primary-600`.
 */

interface ToggleProps {
  enabled: boolean;
  onToggle: (newEnabled: boolean) => void;
  /** Renders a clickable label beside the switch. */
  label?: string;
  /** Muted second line under the label. */
  description?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  className?: string;
  /** Used when there is no visible `label`. */
  ariaLabel?: string;
  /** Skips `t()` for callers passing already-translated text. */
  translate?: boolean;
}

const Toggle: React.FC<ToggleProps> = ({
  enabled,
  onToggle,
  label,
  description,
  disabled = false,
  size = 'medium',
  className,
  ariaLabel,
  translate = true,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const labelId = label ? `${generatedId}-label` : undefined;
  const descriptionId = description ? `${generatedId}-description` : undefined;
  const tr = (value: string) => (translate ? t(value) : value);

  const small = size === 'small';

  const control = (
    <button
      onClick={() => !disabled && onToggle(!enabled)}
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label ? undefined : ariaLabel}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      disabled={disabled}
      className={clsx(
        'relative inline-flex flex-shrink-0 items-center rounded-full transition-colors duration-300',
        FOCUS_RING_ONLY,
        small ? 'h-5 w-9' : 'h-6 w-11',
        // ON is a soft champagne track with a champagne edge and a champagne
        // knob - not a saturated gold fill. At ~18 call sites the solid track
        // was the most-repeated saturated surface in the app, which is exactly
        // what FORM_SPEC §1 reserves for elements a few pixels across. The
        // tint + tinted border + tinted knob still read unambiguously as on.
        enabled
          ? 'bg-champagne/30 border border-champagne/50'
          : 'bg-surface-sunken border border-hairline',
        disabled && 'opacity-50 cursor-not-allowed',
        // Without a label the wrapper is absent, so className lands here.
        !label && !description && className
      )}
    >
      <span
        className={clsx(
          'inline-block transform rounded-full shadow-sm transition-transform duration-300',
          small ? 'h-3.5 w-3.5' : 'h-4 w-4',
          // The knob carries the accent when on; the track only tints.
          enabled ? 'bg-champagne' : 'bg-white',
          small
            ? enabled
              ? 'translate-x-[18px] rtl:-translate-x-[18px]'
              : 'translate-x-1 rtl:-translate-x-1'
            : enabled
              ? 'translate-x-6 rtl:-translate-x-6'
              : 'translate-x-1 rtl:-translate-x-1'
        )}
      />
    </button>
  );

  if (!label && !description) return control;

  return (
    <div className={clsx('flex items-start gap-3', disabled && 'opacity-60', className)}>
      {control}
      <div className="min-w-0">
        {label && (
          <span
            id={labelId}
            onClick={() => !disabled && onToggle(!enabled)}
            className={clsx(
              FIELD_LABEL_CLASS,
              'leading-5',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            {tr(label)}
          </span>
        )}
        {description && (
          <p id={descriptionId} className={clsx('mt-0.5', FIELD_HELP_CLASS)}>
            {tr(description)}
          </p>
        )}
      </div>
    </div>
  );
};

export default Toggle;
